import { useMemo, useEffect, useRef } from "react";
import {
  useForm,
  type UseFormReturn,
  type FieldValues,
  type SubmitHandler,
  type DefaultValues,
} from "react-hook-form";
import * as Form from "@radix-ui/react-form";
import Modal, { type ModalProps } from "@/components/modal/Modal";
import { Flex, Button } from "@radix-ui/themes";

type FormModalProps<TForm extends FieldValues> = Omit<
  ModalProps,
  "children" | "footer"
> & {
  defaultValues: DefaultValues<TForm>;
  onSubmit: SubmitHandler<TForm>;
  submitLabel?: string;
  cancelLabel?: string;
  cancelButtonClassName?: string;
  submitButtonColor?: React.ComponentProps<typeof Button>["color"];
  render: (form: UseFormReturn<TForm>) => React.ReactNode;
};

export function FormModal<TForm extends FieldValues>({
  defaultValues,
  onSubmit,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  cancelButtonClassName,
  submitButtonColor = "gray",
  render,
  open,
  onOpenChange,
  ...rest
}: FormModalProps<TForm>) {
  const form = useForm<TForm>({ defaultValues });
  const prevOpenRef = useRef(open);

  // Reset form when modal opens
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      form.reset(defaultValues);
    }
    prevOpenRef.current = open;
  }, [open, defaultValues, form]);

  const footer = useMemo(
    () => (
      <Flex className="items-center gap-[20px]">
        <Button
          variant="ghost"
          color="gray"
          className={cancelButtonClassName}
          onClick={() => onOpenChange(false)}
        >
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          color={submitButtonColor}
          size="2"
          radius="full"
          className="h-[45px] rounded-full"
          onClick={form.handleSubmit(onSubmit)}
        >
          {submitLabel}
        </Button>
      </Flex>
    ),
    [
      cancelLabel,
      cancelButtonClassName,
      onOpenChange,
      submitLabel,
      submitButtonColor,
      form,
      onSubmit,
    ],
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange} footer={footer} {...rest}>
      <Form.Root
        onSubmit={form.handleSubmit(onSubmit)}
        onReset={() => form.reset()}
      >
        {render(form)}
      </Form.Root>
    </Modal>
  );
}

export default FormModal;
